<template>
  <div class="cc-root">
    <div class="cc-head">
      <span class="cc-eyebrow">✦ Ask Mystic AIda</span>
      <h3 class="cc-title">Feedback &amp; Ideas</h3>
    </div>

    <div ref="logEl" class="cc-log">
      <div v-for="(m, i) in messages" :key="i" class="cc-msg" :class="m.role">
        <p class="cc-bubble">{{ m.text }}</p>
        <div v-if="m.recommend" class="cc-cta">
          <a class="cc-cta-btn" :href="`/?reader=${m.recommend}`">Visit this Mystic →</a>
        </div>
        <div v-if="m.confirm" class="cc-cta">
          <button class="cc-cta-btn" :disabled="m.confirm.done" @click="confirmAppend(m)">
            {{ m.confirm.done ? m.confirm.label : `Add “${m.confirm.title}” to the ideas board` }}
          </button>
        </div>
      </div>
      <p v-if="loading" class="cc-typing">Mystic AIda is reflecting…</p>
    </div>

    <form class="cc-input" @submit.prevent="send">
      <input v-model="draft" :disabled="loading"
             placeholder="Ask how something works, report an issue, or share an idea…" />
      <button type="submit" :disabled="loading || !draft.trim()" aria-label="Send">
        <i class="fas fa-paper-plane"></i>
      </button>
    </form>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue'
import { store } from '@/store.js'
import { conciergeChat, conciergeConfirm } from '@/api-service.js'

const emit = defineEmits(['require-login', 'idea-added'])

const messages = ref([{
  role: 'aida',
  text: "Hi - I'm Mystic AIda. Ask me how anything works, tell me what's broken, or share an idea. I'll point you the right way, flag problems to the team, or add good ideas to the board.",
}])
const draft = ref('')
const loading = ref(false)
const threadId = ref(null)
const logEl = ref(null)

async function scrollDown() {
  await nextTick()
  if (logEl.value) logEl.value.scrollTop = logEl.value.scrollHeight
}

async function send() {
  const text = draft.value.trim()
  if (!text || loading.value) return
  if (!store.querent_id) { emit('require-login'); return }
  messages.value.push({ role: 'seeker', text })
  draft.value = ''
  loading.value = true
  scrollDown()
  try {
    const r = await conciergeChat(text, threadId.value)
    threadId.value = r.thread_id
    const msg = { role: 'aida', text: r.reply }
    if (r.lane === 'available' && r.recommend_reader) msg.recommend = r.recommend_reader
    if (r.lane === 'append' && r.needs_confirmation && r.proposed_idea) {
      msg.confirm = { title: r.proposed_idea, done: false, label: '' }
    }
    messages.value.push(msg)
  } catch (e) {
    messages.value.push({ role: 'aida', text: 'Something went sideways on my end - try again in a moment.' })
  } finally {
    loading.value = false
    scrollDown()
  }
}

async function confirmAppend(m) {
  if (m.confirm.done) return
  try {
    const r = await conciergeConfirm({ lane: 'append', title: m.confirm.title, thread_id: threadId.value })
    m.confirm.done = true
    m.confirm.label = r.action === 'upvoted' ? 'Added your vote ✓' : 'Added to the board ✓'
    emit('idea-added')
  } catch (e) {
    m.confirm.label = 'Could not add - try again'
  }
}

onMounted(scrollDown)
</script>

<style scoped>
/* Content only — the floating scrim, panel chrome, × and slide transition come from
   the shared Conjure overlay shell in CommunityVoice (cv-overlay / cv-form / cv-slide). */
.cc-root { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.cc-head { margin-bottom: 12px; padding-right: 28px; }
.cc-eyebrow { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: #FFDFA3; }
.cc-title { font-family: 'Cinzel', serif; font-size: 20px; font-style: italic; color: #FFDFA3; margin: 2px 0 0; }
.cc-log { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; padding: 6px 2px; }
.cc-msg { display: flex; flex-direction: column; max-width: 88%; }
.cc-msg.seeker { align-self: flex-end; align-items: flex-end; }
.cc-msg.aida { align-self: flex-start; align-items: flex-start; }
.cc-bubble { margin: 0; font-size: 13.5px; line-height: 1.55; padding: 10px 14px; border-radius: 14px; }
.cc-msg.aida .cc-bubble { background: rgba(60,40,104,0.4); border: 1px solid rgba(176,134,238,0.28); color: #ece3ff; }
.cc-msg.seeker .cc-bubble { background: rgba(102,51,153,0.4); border: 1px solid rgba(240,217,163,0.25); color: #fff7ea; }
.cc-cta { margin-top: 7px; }
.cc-cta-btn { font-family: 'Cinzel', serif; font-size: 12px; color: #1a0f2e; background: linear-gradient(180deg,#FFDFA3,#cdae6a);
  border: none; border-radius: 9px; padding: 7px 14px; cursor: pointer; text-decoration: none; display: inline-block; }
.cc-cta-btn:disabled { opacity: 0.7; cursor: default; background: rgba(127,212,154,0.25); color: #d6f5e1; }
.cc-typing { font-size: 12px; font-style: italic; color: rgba(195,169,255,0.7); margin: 0; }
.cc-input { display: flex; gap: 8px; margin-top: 12px; }
.cc-input input { flex: 1; background: rgba(20,8,40,0.6); border: 1px solid rgba(155,134,201,0.35);
  border-radius: 10px; padding: 11px 14px; color: #ece3ff; font-size: 13.5px; }
.cc-input input:focus { outline: none; border-color: rgba(240,217,163,0.5); }
.cc-input button { flex: none; width: 44px; border-radius: 10px; border: none; cursor: pointer;
  background: linear-gradient(180deg,#FFDFA3,#cdae6a); color: #1a0f2e; font-size: 15px; }
.cc-input button:disabled { opacity: 0.5; cursor: default; }
</style>
